import _defineProperty from "/build/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import calcAge from "@/lib/calcAge";
import { errorMessages, GENDER, metaSchemas, userRating } from "@/lib/constants";
import dayjs from "@/lib/day";
import { AUTH_ERROR_MESSAGES } from "@/lib/errors";
import getRemoteIpAddress from "@/lib/getRemoteIpAddress";
import { accountEditDuplicateAddress, accountEditPincodeMail, duplicateAddress, passwordResetComplete, passwordResetTokenMail, passwordUpdateComplete, profilePinResetTokenMail, registerPincodeMail, signupComplete, userUpdate, withdrawalComplete } from "@/lib/mailTemplate";
import { isEnded } from "@/lib/metaUtil";
import PasswordToken from "@/lib/passwordToken";
import PINCode from "@/lib/pinCode";
import sendEmail from "@/lib/sendEmail";
import UserToken from "@/lib/userToken";
import { gql } from "@apollo/client";
import { composeResolvers } from "@graphql-tools/resolvers-composition";
import { UserInputError } from "apollo-server-errors";
import { createPageInfo, createToken, isAuthenticated, ownerOnly, metaResolveType, sleep, values, logicaErrorCode } from "./helper";
import { passwordFormatValidate } from "@/lib/yupPasswordValidate";
import Uid2Token from "@/lib/uid2Token";
import { birthdayValidate } from "@/lib/birthdayValidate";
import ChatGuideLineAgreement from "@/lib/chatGuideLineAgreement";
function passwordValidate(password) {
  return password && password.length >= 8 && passwordFormatValidate(password);
}
export const typeDef = gql`
  interface User {
    uuid: ID!
    id: Int
    display_name: String
    birthday: String
    sex: Int
    type: String
  }

  type AccountUser implements User @cacheControl(scope: PRIVATE, maxAge: 0) {
    uuid: ID!
    id: Int
    display_name: String
    birthday: String
    age: Int
    sex: Int
    type: String
    mail_address: String
    first_name: String
    last_name: String
    fullName: String
    rights_free_flag: Boolean
    currentProfile: ProfileUser
    profiles: [ProfileUser]
    test_flg: Boolean
    hashedUserId: String
    receiveAdvertisingMailType: Int
  }

  union FavoriteMeta = EpisodeMeta | LiveMeta | TVShowMeta | ChannelMeta

  type UserFavorites @cacheControl(maxAge: 0) {
    id: Int
    metas: [FavoriteMeta]
  }

  type ProfileUser implements User @cacheControl(scope: PRIVATE, maxAge: 0) {
    uuid: ID!
    id: Int
    display_name: String
    birthday: String
    age: Int
    sex: Int
    type: String
    passcode: String
    ownerProfile: Boolean
    needPasscode: Boolean
    icon: Int
    rating: Int
  }

  type Auth {
    token: String!
    user: AccountUser
  }

  type UserUpdate {
    user: AccountUser
    error: String
  }

  type Profile @cacheControl(scope: PRIVATE, maxAge: 0) {
    profile: ProfileUser
  }

  type AuthResult @cacheControl(scope: PRIVATE, maxAge: 0) {
    result: Boolean
  }

  type UserConnection {
    user: AccountUser
    profiles: [ProfileUser]
  }

  type PinCodeResult {
    result: Boolean
  }

  type VerifyResult {
    verified: Boolean
    error: String
  }

  type FavoriteResult @cacheControl(maxAge: 0) {
    result: Boolean
    favorites: UserFavorites
  }

  type FavoritesByContentTypeResult @cacheControl(maxAge: 0) {
    data: [FavoriteMeta]
    pageInfo: PageInfo
  }

  type ViewingHistoriesResult @cacheControl(scope: PRIVATE, maxAge: 0) {
    data: [MediaMeta]
    pageInfo: PageInfo
  }

  type AddResumeResult {
    result: Boolean
  }

  type UserDeleteResponse @cacheControl(scope: PRIVATE) {
    result: Boolean
  }
  type AuthOwnerProfileResponse @cacheControl(scope: PRIVATE) {
    result: Boolean
  }

  type DeleteProfileResponse @cacheControl(scope: PRIVATE) {
    result: Boolean
  }

  type DeleteViewingHistoryResult @cacheControl(scope: PRIVATE, maxAge: 0) {
    result: Boolean
  }

  # type BroadcastDiscountResult @cacheControl(scope: PRIVATE, maxAge: 0) {
  #   result: Boolean
  #   user_voucher_id: Int
  # }

  type AdvertisingPreferencesResult @cacheControl(scope: PRIVATE, maxAge: 0) {
    request_id: String
  }

  type Query {
    user: AccountUser

    userFavorites: UserFavorites

    favoritesByContentType(
      type: String
      offset: Int
      limit: Int
    ): FavoritesByContentTypeResult

    viewingHistories(
      offset: Int
      limit: Int
      sort: String
      nocache: Boolean
    ): ViewingHistoriesResult

    # broadcastDiscount(courseId: Int, productId: Int): BroadcastDiscountResult

    checkTokenTimeout: AuthResult
  }

  type Mutation {
    login(
      loginid: String!
      password: String!
      signature: String!
    ): Auth

    logout(force: Boolean): AuthResult

    selectProfile(profileId: Int, passcode: String, type: String): AccountUser

    signupPincode(mailAddress: String!): PinCodeResult

    sendPincode(mailAddress: String!): PinCodeResult

    sendProfilePinResetToken: PinCodeResult

    sendPasswordResetToken(mailAddress: String!): PinCodeResult

    verifyPinCode(code: String!, mailAddress: String!): VerifyResult

    verifyPasswordToken(token: String!): VerifyResult

    signup(
      code: String!
      mailAddress: String!
      password: String!
      firstName: String!
      lastName: String!
      birthday: String!
      sex: String!
      terms: String!
      signature: String!
    ): Auth

    addFavorite(id: Int!): FavoriteResult

    deleteFavorite(id: Int!): FavoriteResult

    addResume(
      resume: Int
      metaId: Int
      groupedMetaId: Int
      psid: String
      reachToEnd: Boolean
    ): AddResumeResult

    update(mailAddress: String, code: String): UserUpdate

    updatePassword(currentPassword: String, password: String): UserUpdate

    updateOwnerProfile(
      displayName: String
      icon: Int
      passcode: String
      rating: Int
    ): UserUpdate

    passwordReset(password: String!, token: String!): PinCodeResult

    passcodeReset(passcode: String!, token: String!): PinCodeResult

    addProfile(
      displayName: String
      birthday: String
      sex: String
      icon: Int
      rating: Int
      passcode: String
    ): Profile

    updateProfile(
      displayName: String
      birthday: String
      sex: String
      password: String
      profileId: Int
      icon: Int
      rating: Int
      passcode: String
    ): Profile

    deleteProfile(profileId: Int): DeleteProfileResponse

    userDelete: UserDeleteResponse

    authProfile(profileId: Int, passcode: String): AuthOwnerProfileResponse

    deleteViewingHistory(metaIds: [String]): DeleteViewingHistoryResult

    advertisingPreferences(
      receiveAdvertisingMailFlag: Boolean
    ): AdvertisingPreferencesResult

    refreshToken: AuthResult
  }
`;
async function getProfiles(parent, dataSources) {
  const profiles = await (async () => {
    if (!(parent !== null && parent !== void 0 && parent.profiles)) {
      const res = await dataSources.user.me();
      return [res.user, ...(res === null || res === void 0 ? void 0 : res.profiles)];
    } else {
      const profileIds = parent.profiles.map(p => p.id);
      if (profileIds.includes(parent.id)) {
        return [...parent.profiles];
      } else {
        return [parent, ...parent.profiles];
      }
    }
  })();
  return profiles;
}
function getFavoriteMetas(dataSources) {
  try {
    const res = dataSources.user.allFavorites({
      offset: 0,
      limit: 50
    });
    return res;
  } catch (e) {
    console.log("getFavoriteMetas", e);
    return null;
  }
}
async function deleteToken(req, res) {
  const userToken = new UserToken(req, res);
  await userToken.delete();
  const chatGuideLineAgreement = new ChatGuideLineAgreement(req, res);
  await chatGuideLineAgreement.delete();
  const uid2 = new Uid2Token(req, res);
  uid2.delete();
  req.userToken = null;
}
export const resolvers = composeResolvers({
  Query: {
    user: async (_parent, _args, _ref) => {
      let {
        req,
        res,
        dataSources
      } = _ref;
      if (req.userToken) {
        try {
          const result = await dataSources.user.me();
          const {
            user
          } = result;
          user.profiles = result.profiles;
          // hashedUserIdがなかったらユーザートークンを更新する
          // レコメンド対応で必要だが、リリース時にログインしていたユーザーのuserTokenからは取得できないため
          if (!req.userToken.hashedUserId) {
            var _user$values;
            const userToken = new UserToken(req, res);
            await userToken.refresh({
              hashedUserId: user === null || user === void 0 || (_user$values = user.values) === null || _user$values === void 0 ? void 0 : _user$values.user_id_hash
            });
          }
          return user;
        } catch (e) {
          console.log(e);
          await deleteToken(req, res);
          return null;
        }
      }
      return null;
    },
    userFavorites: async (_parent, _args, _ref2) => {
      let {
        req,
        dataSources
      } = _ref2;
      if (req.userToken) {
        try {
          const favoriteMetas = await getFavoriteMetas(dataSources);
          const id = req.userToken.currentProfile || req.userToken.id;
          return {
            id,
            metas: favoriteMetas
          };
        } catch (e) {
          return {
            metas: []
          };
        }
      }
    },
    // お気に入り取得
    favoritesByContentType: async (_parent, args, _ref3) => {
      let {
        req,
        dataSources
      } = _ref3;
      const {
        type,
        offset,
        limit
      } = args;
      const metaSchemaId = (() => {
        if (type === "channel") {
          return metaSchemas.channel.id;
        } else if (type === "tvshow") {
          return metaSchemas.tvshow.id;
        } else {
          return metaSchemas.episode.id;
        }
      })();
      const dataLimit = 200;
      const res = await dataSources.user.favoritesByMetaSchemaId({
        offset: 0,
        limit: dataLimit,
        metaSchemaId: metaSchemaId
      });
      let favoriteMetas = res.user_favorites.map(fav => fav.meta).filter(meta => !!meta);
      const now = dayjs();
      const deliveryDateFilter = meta => {
        if (meta.delivery_end_at) {
          return now.isBefore(meta.delivery_end_at);
        }
        return true;
      };
      if (type === "vod") {
        favoriteMetas = favoriteMetas.filter(meta => {
          var _meta$values;
          return !(meta !== null && meta !== void 0 && (_meta$values = meta.values) !== null && _meta$values !== void 0 && _meta$values.is_live);
        }).filter(deliveryDateFilter);
      } else if (type === "catchup") {
        favoriteMetas = favoriteMetas.filter(meta => {
          var _meta$values2, _meta$values3, _meta$values4;
          if (!(meta !== null && meta !== void 0 && (_meta$values2 = meta.values) !== null && _meta$values2 !== void 0 && _meta$values2.is_live)) return false;
          if (!(meta !== null && meta !== void 0 && (_meta$values3 = meta.values) !== null && _meta$values3 !== void 0 && _meta$values3.catch_up_flag)) return false;
          const encodeEnded = isEnded(meta === null || meta === void 0 || (_meta$values4 = meta.values) === null || _meta$values4 === void 0 ? void 0 : _meta$values4.encode_end_date);
          if (!encodeEnded) return false;
          const deliveryEnded = isEnded(meta === null || meta === void 0 ? void 0 : meta.delivery_end_at);
          if (deliveryEnded) return false;
          return true;
        }).filter(deliveryDateFilter);
      } else if (type === "live") {
        favoriteMetas = favoriteMetas.filter(meta => {
          var _meta$values5, _meta$values6;
          if (!(meta !== null && meta !== void 0 && (_meta$values5 = meta.values) !== null && _meta$values5 !== void 0 && _meta$values5.is_live)) {
            return false;
          }
          const ended = isEnded(meta === null || meta === void 0 || (_meta$values6 = meta.values) === null || _meta$values6 === void 0 ? void 0 : _meta$values6.encode_end_date);
          return !ended;
        }).filter(deliveryDateFilter);
      } else if (type === "episode") {
        //  VODと見逃しのみ表示
        favoriteMetas = favoriteMetas.filter(meta => {
          var _meta$values7, _meta$values8, _meta$values9;
          if (!(meta !== null && meta !== void 0 && (_meta$values7 = meta.values) !== null && _meta$values7 !== void 0 && _meta$values7.is_live)) return true;
          const ended = isEnded(meta === null || meta === void 0 || (_meta$values8 = meta.values) === null || _meta$values8 === void 0 ? void 0 : _meta$values8.encode_end_date);
          return (meta === null || meta === void 0 || (_meta$values9 = meta.values) === null || _meta$values9 === void 0 ? void 0 : _meta$values9.catch_up_flag) && ended;
        }).filter(deliveryDateFilter);
      }
      favoriteMetas = favoriteMetas.filter(meta => {
        const rating = req.userToken.rating;
        const permitRating = userRating[rating].permitRating;
        return permitRating.includes(meta.values.rating);
      });
      const totalCount = favoriteMetas.length;
      favoriteMetas = favoriteMetas.slice(offset, offset + limit);
      return {
        data: favoriteMetas,
        pageInfo: createPageInfo({
          limit,
          offset,
          count: totalCount
        })
      };
    },
    viewingHistories: async (_parent, _ref4, _ref5) => {
      let {
        offset,
        limit,
        sort,
        nocache
      } = _ref4;
      let {
        dataSources
      } = _ref5;
      try {
        const res = await dataSources.user.getViewingHistory({
          offset,
          limit,
          sort,
          nocache
        });
        let metas = res.viewing_histories.map(vh => {
          return _objectSpread(_objectSpread({}, vh.meta), {}, {
            resume_point: vh.resume_point,
            last_viewing_at: vh.last_viewing_at
          });
        });
        return {
          data: metas,
          pageInfo: createPageInfo({
            limit,
            offset,
            count: res.total_count
          })
        };
      } catch (e) {
        console.log("getViewingHistory error", e);
      }
    },
    // broadcastDiscount: async (_parent, args, { dataSources }) => {
    //   try {
    //     const res = await dataSources.user.broadcastDiscount(args);

    //     console.log("broadcastDiscount", res);
    //     return res;
    //   } catch (e) {
    //     console.log("delete broadcastDiscount error", e);
    //   }
    // },
    checkTokenTimeout: async (_parent, _args, _ref6) => {
      let {
        req,
        res
      } = _ref6;
      try {
        const userToken = new UserToken(req, res);
        const result = await userToken.checkExpiration();
        return {
          result: result
        };
      } catch (e) {
        console.log("checkTokenTimeout error", e);
        return {
          result: false
        };
      }
    }
  },
  Mutation: {
    // ログイン
    login: async (_parent, _ref7, _ref8) => {
      let {
        loginid,
        password,
        signature
      } = _ref7;
      let {
        req,
        res,
        dataSources
      } = _ref8;
      try {
        const user = await dataSources.user.login(loginid, password);
        const token = await createToken(req, res, user, signature);
        const uid2 = new Uid2Token(req, res);
        await uid2.getToken();
        return {
          token,
          user: user.user
        };
      } catch (e) {
        var _e$extensions, _response$error;
        console.log("login auth error", e);
        // console.log(JSON.stringify(e.extentions, null, 2));
        const response = e === null || e === void 0 || (_e$extensions = e.extensions) === null || _e$extensions === void 0 || (_e$extensions = _e$extensions.response) === null || _e$extensions === void 0 ? void 0 : _e$extensions.body;
        if (response && (response === null || response === void 0 || (_response$error = response.error) === null || _response$error === void 0 ? void 0 : _response$error.code) == 10006) {
          throw new UserInputError(AUTH_ERROR_MESSAGES["acountlock"]);
        } else {
          throw new UserInputError(AUTH_ERROR_MESSAGES["unauthorized"]);
        }
      }
    },
    // ログアウト
    logout: async (_parent, _ref9, _ref10) => {
      let {
        force
      } = _ref9;
      let {
        req,
        res,
        dataSources
      } = _ref10;
      try {
        const {
          result
        } = await dataSources.user.logout();
        if (result) {
          await deleteToken(req, res);
        }
        return {
          result
        };
      } catch (e) {
        const errorCode = logicaErrorCode(e);
        console.log(`logout error errorCode: ${errorCode}`);
        return {
          result: false
        };
      } finally {
        if (force) {
          await deleteToken(req, res);
        }
      }
    },
    // プロフィールを選択
    selectProfile: async (parent, args, _ref11) => {
      let {
        req,
        res,
        dataSources
      } = _ref11;
      // args : profileId, passcode, type
      // type : auth, no-auth
      const userToken = new UserToken(req, res);
      const token = await userToken.verify();
      if (token) {
        const res = await dataSources.user.me();
        const profiles = await getProfiles(parent, dataSources);
        const {
          user
        } = res;
        const selectProfile = profiles.find(profile => profile.id == args.profileId);
        if (selectProfile) {
          var _selectProfile$values, _selectProfile$values2, _selectProfile$values3;
          if (args.type == "auth" && (_selectProfile$values = selectProfile.values) !== null && _selectProfile$values !== void 0 && _selectProfile$values.passcode && ((_selectProfile$values2 = selectProfile.values) === null || _selectProfile$values2 === void 0 ? void 0 : _selectProfile$values2.passcode) != args.passcode) {
            throw new UserInputError(AUTH_ERROR_MESSAGES["invalidPasscode"]);
          }
          user.profiles = profiles;
          user.selectProfileId = args.profileId;
          await userToken.refresh({
            currentProfile: args.profileId,
            rating: (_selectProfile$values3 = selectProfile.values) === null || _selectProfile$values3 === void 0 ? void 0 : _selectProfile$values3.rating
          });
          return user;
        }
      }
      return null;
    },
    // 会員登録時のPINコード送信
    signupPincode: async (_parent, _ref12, _ref13) => {
      let {
        mailAddress
      } = _ref12;
      let {
        req,
        res,
        dataSources
      } = _ref13;
      try {
        const user = await dataSources.user.existed(mailAddress);
        if (user) {
          // ユーザーがすでに存在していたらメールを送る
          await sendEmail(duplicateAddress({
            address: mailAddress
          }));
        } else {
          const pinCode = new PINCode(req, res);
          const pin = await pinCode.publish(mailAddress);
          // console.log(mailAddress);
          // console.log("pincode", pin);
          await sendEmail(registerPincodeMail({
            address: mailAddress,
            pincode: pin
          }));
        }
        return {
          result: true
        };
      } catch (e) {
        console.log("pincode send error", JSON.stringify(e));
        throw new UserInputError(AUTH_ERROR_MESSAGES["default"]);
      }
    },
    // メールアドレス変更PINコード送信
    sendPincode: async (_parent, _ref14, _ref15) => {
      let {
        mailAddress
      } = _ref14;
      let {
        req,
        res,
        dataSources
      } = _ref15;
      const user = await dataSources.user.existed(mailAddress);
      if (user) {
        await sendEmail(accountEditDuplicateAddress({
          address: mailAddress
        }));
        return {
          result: false
        };
      } else {
        const pinCode = new PINCode(req, res);
        const pin = await pinCode.publish(mailAddress);
        await sendEmail(accountEditPincodeMail({
          address: mailAddress,
          pincode: pin
        }));
        return {
          result: true
        };
      }
    },
    // プロフィール暗証番号リセットURLの送信
    sendProfilePinResetToken: async (_parent, _args, _ref16) => {
      let {
        req,
        res,
        dataSources
      } = _ref16;
      const {
        user
      } = await dataSources.user.me();
      const mailAddress = user.mail_address;
      const passwordToken = new PasswordToken(req, res);
      const token = await passwordToken.publish(mailAddress);
      await sendEmail(profilePinResetTokenMail({
        address: mailAddress,
        token: token
      }));
      return {
        result: true
      };
    },
    // パスワードリセットメールの送信
    sendPasswordResetToken: async (_parent, _ref17, _ref18) => {
      let {
        mailAddress
      } = _ref17;
      let {
        req,
        res,
        dataSources
      } = _ref18;
      const user = await dataSources.user.existed(mailAddress);
      if (user) {
        // ユーザーが存在していたらTokenメールを送る
        const passwordToken = new PasswordToken(req, res);
        const token = await passwordToken.publish(mailAddress);
        await sendEmail(passwordResetTokenMail({
          address: mailAddress,
          token: token
        }));
      }
      return {
        result: true
      };
    },
    // PINコード検証
    verifyPinCode: async (_parent, args, _ref19) => {
      let {
        req,
        res
      } = _ref19;
      const pinCode = new PINCode(req, res);
      const verified = await pinCode.verify(args.code, args.mailAddress);
      if (!verified) {
        throw new UserInputError(AUTH_ERROR_MESSAGES["pincode"], {
          invalidArgs: Object.keys(args)
        });
      }
      return {
        verified: verified
      };
    },
    // passwordToken検証
    verifyPasswordToken: async (_parent, _ref20, _ref21) => {
      let {
        token
      } = _ref20;
      let {
        req,
        res
      } = _ref21;
      const passwordToken = new PasswordToken(req, res);
      const verified = await passwordToken.verify(token);
      if (!verified) {
        console.log("verifyPasswordToken error", AUTH_ERROR_MESSAGES["passwordToken"]);
        // } else {
        //   await passwordToken.delete(token);
      }

      return {
        verified: verified
      };
    },
    // サインアップ
    signup: async (_parent, args, _ref22) => {
      let {
        req,
        res,
        dataSources
      } = _ref22;
      try {
        console.log("############### signup mutation start ###############");
        // パスワードバリデート
        if (!passwordValidate(args.password)) {
          throw new UserInputError(errorMessages["badPassword"], {
            invalidArgs: ["password"]
          });
        }
        // 生年月日をバリデート
        if (!birthdayValidate(args === null || args === void 0 ? void 0 : args.birthday)) {
          throw new UserInputError(errorMessages["signup"]);
        }
        // ここでもメールアドレスが認証済みかの確認をする
        const pinCode = new PINCode(req, res);
        const verified = await pinCode.verify(args.code, args.mailAddress);
        if (!verified) {
          throw new UserInputError(AUTH_ERROR_MESSAGES["pincode"], {
            invalidArgs: Object.keys(args)
          });
        }
        // ユーザーがすでに存在しているか
        const existsUser = await dataSources.user.existed(args.mailAddress);
        if (existsUser) {
          // console.log("user existed email: ", args.mailAddress);
          throw new UserInputError(AUTH_ERROR_MESSAGES["signup"]);
        }
        const ua = req.headers["user-agent"];
        const clientIp = getRemoteIpAddress(req);
        console.log("ua: ", ua);
        console.log("ip: ", clientIp);
        console.log("signup api request");
        const user = await dataSources.user.signup(args);
        if (user) {
          console.log("user registered");
          console.log("userId: ", user.id);
        }
        pinCode.delete(args.mailAddress);
        const token = await createToken(req, res, user, args.signature, false);
        console.log("agreedterms: ", args.terms);
        // 規約の同意
        const agreeTerms = args.terms.split(",");
        await Promise.all(agreeTerms.map(async key => {
          return await dataSources.terms.agree(key);
        }));
        await sendEmail(signupComplete({
          address: args.mailAddress,
          user: user.user
        }));
        const uid2 = new Uid2Token(req, res);
        await uid2.getToken();
        console.log("############### signup mutation end ###############");
        return {
          token,
          user: user.user
        };
      } catch (e) {
        console.log("signup error", e);
        if (e instanceof UserInputError) {
          throw e;
        } else {
          throw new UserInputError(AUTH_ERROR_MESSAGES["signup"]);
        }
      }
    },
    // お気に入り追加
    addFavorite: async (_parent, _ref23, _ref24) => {
      let {
        id
      } = _ref23;
      let {
        req,
        dataSources
      } = _ref24;
      const {
        result
      } = await dataSources.user.addFavorite(id);
      await sleep(200);
      const favoriteMetas = await getFavoriteMetas(dataSources);
      const userId = req.userToken.currentProfile || req.userToken.id;
      return {
        result,
        favorites: {
          id: userId,
          metas: favoriteMetas
        }
      };
    },
    // お気に入り削除
    deleteFavorite: async (_parent, _ref25, _ref26) => {
      let {
        id
      } = _ref25;
      let {
        req,
        dataSources
      } = _ref26;
      const {
        result
      } = await dataSources.user.deleteFavorite(id);
      await sleep(200);
      const favoriteMetas = await getFavoriteMetas(dataSources);
      const userId = req.userToken.currentProfile || req.userToken.id;
      return {
        result,
        favorites: {
          id: userId,
          metas: favoriteMetas
        }
      };
    },
    // 視聴履歴、ブックマーク追加
    addResume: async (_parent, args, _ref27) => {
      let {
        dataSources
      } = _ref27;
      try {
        await dataSources.user.addViewingHistory(args);
        await dataSources.user.addViewingBookmark(args);
        return {
          result: true
        };
      } catch (e) {
        console.log("addResume error", JSON.stringify(e, null, 2));
        return {
          result: false
        };
      }
    },
    update: async (_parent, _ref28, _ref29) => {
      let {
        mailAddress,
        code
      } = _ref28;
      let {
        req,
        res,
        dataSources
      } = _ref29;
      try {
        var _user$user;
        // メールアドレスが認証済みかの確認をする
        const pinCode = new PINCode(req, res);
        const verified = await pinCode.verify(code, mailAddress);
        if (!verified) {
          throw new UserInputError(errorMessages["pincode"], {
            invalidArgs: Object.keys({
              mailAddress,
              code
            })
          });
        }
        const user = await dataSources.user.update({
          mailAddress
        });
        pinCode.delete(mailAddress);
        await sendEmail(userUpdate({
          address: user === null || user === void 0 || (_user$user = user.user) === null || _user$user === void 0 ? void 0 : _user$user.mail_address,
          user: user.user
        }));
        return {
          user: user.user
        };
      } catch (e) {
        console.log("update error", e);
        if (e instanceof UserInputError) {
          throw e;
        } else {
          throw new UserInputError(errorMessages["update"]);
        }
      }
    },
    updatePassword: async (_parent, _ref30, _ref31) => {
      let {
        currentPassword,
        password
      } = _ref30;
      let {
        req,
        dataSources
      } = _ref31;
      try {
        // パスワード確認
        const check = await dataSources.user.checkPassword(currentPassword);
        if (check.result) {
          var _user$user2;
          // パスワードバリデート
          if (!passwordValidate(password)) {
            throw new UserInputError(errorMessages["badPassword"], {
              invalidArgs: ["password"]
            });
          }
          const user = await dataSources.user.updatePassword(password);
          const mailAddress = user === null || user === void 0 || (_user$user2 = user.user) === null || _user$user2 === void 0 ? void 0 : _user$user2.mail_address;
          await sendEmail(passwordUpdateComplete({
            address: mailAddress
          }));
          return {
            user: user.user
          };
        } else {
          throw new UserInputError(errorMessages["invalidPassword"]);
        }
      } catch (e) {
        console.log("update password error", e);
        if (e instanceof UserInputError) {
          throw e;
        } else {
          throw new UserInputError(errorMessages["updatePassword"]);
        }
      }
    },
    passwordReset: async (_parent, _ref32, _ref33) => {
      let {
        token,
        password
      } = _ref32;
      let {
        req,
        res,
        dataSources
      } = _ref33;
      try {
        const passwordToken = new PasswordToken(req, res);
        const mailAddress = await passwordToken.getValue(token);
        const user = await dataSources.user.existed(mailAddress);
        const id = user === null || user === void 0 ? void 0 : user.id;
        const result = await dataSources.user.passwordUpdate({
          userId: id,
          password
        });
        await passwordToken.delete(token);
        if (result) {
          await sendEmail(passwordResetComplete({
            address: mailAddress
          }));
          return {
            result: true
          };
        } else {
          return {
            result: false
          };
        }
      } catch (e) {
        var _e$extensions2;
        console.log("password reset error", e);
        if (e !== null && e !== void 0 && (_e$extensions2 = e.extensions) !== null && _e$extensions2 !== void 0 && _e$extensions2.response) {
          console.dir(e.extensions.response);
        }
        throw new UserInputError(errorMessages["passwordReset"]);
      }
    },
    passcodeReset: async (_parent, _ref34, _ref35) => {
      let {
        token,
        passcode
      } = _ref34;
      let {
        req,
        res,
        dataSources
      } = _ref35;
      try {
        // パスコードをバリデート
        if (passcode && !/^\d{4}$/.test(passcode)) {
          throw new UserInputError(errorMessages["passcodeReset"]);
        }
        const passwordToken = new PasswordToken(req, res);
        const mailAddress = await passwordToken.getValue(token);
        const {
          user
        } = await dataSources.user.me();
        await passwordToken.delete(token);
        if (user.mail_address === mailAddress) {
          await dataSources.user.updateOwnerProfile({
            passcode
          });
          const userToken = new UserToken(req, res);
          await userToken.refresh();
          return {
            result: true
          };
        } else {
          console.log("update passcode reset mailAddress unmatched.");
          throw new Error("mailAddress unmatched.");
        }
      } catch (e) {
        console.log("update owner passcode reset error", e);
        throw new UserInputError(errorMessages["passcodeReset"]);
      }
    },
    updateOwnerProfile: async (_parent, args, _ref36) => {
      let {
        req,
        res,
        dataSources
      } = _ref36;
      try {
        // パスコードをバリデート
        if (args.passcode && !/^\d{4}$/.test(args.passcode)) {
          throw new UserInputError(errorMessages["profileUpdate"]);
        }
        const user = await dataSources.user.updateOwnerProfile(args);
        const userToken = new UserToken(req, res);
        await userToken.refresh({
          rating: args.rating
        });
        return {
          user: user.user
        };
      } catch (e) {
        console.log("update owner profile error", e);
        throw new UserInputError(errorMessages["profileUpdate"]);
      }
    },
    addProfile: async (_parent, args, _ref37) => {
      let {
        dataSources
      } = _ref37;
      try {
        var _result$profile;
        // パスコードをバリデート
        if (args.passcode && !/^\d{4}$/.test(args.passcode)) {
          throw new UserInputError(errorMessages["profileUpdate"]);
        }
        // 生年月日をバリデート
        if (!birthdayValidate(args === null || args === void 0 ? void 0 : args.birthday)) {
          throw new UserInputError(errorMessages["profileUpdate"]);
        }
        const result = await dataSources.user.addProfile(args);
        // 暫定対応: profiles系のAPIの時だけActiveRecord::Modelがそのままjsonになっているため
        // 性別が文字列で返ってきてしまう
        if (typeof (result === null || result === void 0 || (_result$profile = result.profile) === null || _result$profile === void 0 ? void 0 : _result$profile.sex) === "string") {
          result.profile.sex = GENDER[result.profile.sex];
        }
        return {
          profile: result.profile
        };
      } catch (e) {
        console.log("add profile error", e);
        throw new UserInputError(errorMessages["profile"]);
      }
    },
    updateProfile: async (_parent, args, _ref38) => {
      let {
        dataSources
      } = _ref38;
      try {
        var _result$profile2;
        // パスコードをバリデート
        if (args.passcode && !/^\d{4}$/.test(args.passcode)) {
          throw new UserInputError(errorMessages["profileUpdate"]);
        }
        // 生年月日をバリデート
        if (!birthdayValidate(args === null || args === void 0 ? void 0 : args.birthday)) {
          throw new UserInputError(errorMessages["profileUpdate"]);
        }
        const result = await dataSources.user.updateProfile(args);
        // 暫定対応: profiles系のAPIの時だけActiveRecord::Modelがそのままjsonになっているため
        // 性別が文字列で返ってきてしまう
        if (typeof (result === null || result === void 0 || (_result$profile2 = result.profile) === null || _result$profile2 === void 0 ? void 0 : _result$profile2.sex) === "string") {
          result.profile.sex = GENDER[result.profile.sex];
        }
        return {
          profile: result.profile
        };
      } catch (e) {
        console.log("update profile error", e);
        throw new UserInputError(errorMessages["profileUpdate"]);
      }
    },
    deleteProfile: async (_parent, args, _ref39) => {
      let {
        dataSources
      } = _ref39;
      try {
        const profile = await dataSources.user.deleteProfile(args);
        return profile;
      } catch (e) {
        console.log("delete profile error", e);
        throw new UserInputError(errorMessages["profileDelete"]);
      }
    },
    userDelete: async (_parent, _args, _ref40) => {
      let {
        req,
        res,
        dataSources
      } = _ref40;
      try {
        const {
          user
        } = await dataSources.user.me();
        const {
          result
        } = await dataSources.user.withdrawal();
        const userToken = new UserToken(req, res);
        await userToken.deleteAll();
        req.userToken = null;
        if (result) {
          await sendEmail(withdrawalComplete({
            address: user === null || user === void 0 ? void 0 : user.mail_address,
            user: user
          }));
        }
        return {
          result
        };
      } catch (e) {
        console.log("user delete error", e);
        throw new UserInputError(errorMessages["userDelete"]);
      }
    },
    authProfile: async (parent, args, _ref41) => {
      let {
        req,
        dataSources
      } = _ref41;
      try {
        var _profile$values;
        const profiles = await getProfiles(parent, dataSources);
        const profile = profiles.find(profile => profile.id == args.profileId);
        const passcode = profile === null || profile === void 0 || (_profile$values = profile.values) === null || _profile$values === void 0 ? void 0 : _profile$values.passcode;
        if (passcode == args.passcode) {
          return {
            result: true
          };
        } else {
          return {
            result: false
          };
        }
      } catch (e) {
        console.log("authOwnerProfile error", e);
      }
    },
    deleteViewingHistory: async (_parent, _ref42, _ref43) => {
      let {
        metaIds
      } = _ref42;
      let {
        dataSources
      } = _ref43;
      try {
        const res = await dataSources.user.deleteViewingHistory(metaIds.join(","));
        await sleep(200);
        return res;
      } catch (e) {
        console.log("delete ViewingHistory error", e);
        // throw new UserInputError(errorMessages["deleteViewingHistory"]);
      }
    },

    advertisingPreferences: async (_parent, _ref44, _ref45) => {
      let {
        receiveAdvertisingMailFlag
      } = _ref44;
      let {
        dataSources
      } = _ref45;
      try {
        const res = await dataSources.user.advertisingPreferences({
          receiveAdvertisingMailFlag
        });
        return res;
      } catch (e) {
        console.log("advertisingPreferences error", e);
        throw new UserInputError(errorMessages["advertisingPreferences"]);
      }
    },
    refreshToken: async (_parent, _args, _ref46) => {
      let {
        req,
        res
      } = _ref46;
      try {
        const userToken = new UserToken(req, res);
        const token = await userToken.refresh();
        if (token) {
          return {
            result: true
          };
        } else {
          return {
            result: false
          };
        }
      } catch (e) {
        console.log("refreshToken error", e);
        return {
          result: false
        };
      }
    }
  },
  User: {
    __resolveType(user) {
      switch (user.type) {
        case "account":
          return "AccountUser";
        case "profile":
          return "ProfileUser";
      }
      return "User";
    }
  },
  AccountUser: {
    fullName: user => `${user.last_name} ${user.first_name}`,
    currentProfile: async (parent, _args, _ref47) => {
      var _token$payload;
      let {
        req,
        res,
        dataSources
      } = _ref47;
      const userToken = new UserToken(req, res);
      const token = await userToken.verify();
      if (token && (_token$payload = token.payload) !== null && _token$payload !== void 0 && _token$payload.currentProfile || parent.selectProfileId) {
        const profiles = await getProfiles(parent, dataSources);
        const profileId = token.payload.currentProfile || parent.selectProfileId;
        const profile = profiles.find(profile => profile.id == profileId);
        return profile;
      }
      return null;
    },
    profiles: async (parent, _args, _ref48) => {
      let {
        req,
        dataSources
      } = _ref48;
      const profiles = await getProfiles(parent, dataSources);
      if (profiles) {
        return profiles.sort((a, b) => a.id - b.id);
      }
      return null;
    },
    test_flg: user => values(user, "test_flg"),
    age: user => calcAge(user.birthday),
    hashedUserId: user => values(user, "user_id_hash"),
    receiveAdvertisingMailType: user => values(user, "receive_advertising_mail_type")
  },
  ProfileUser: {
    passcode: profile => values(profile, "passcode"),
    ownerProfile: profile => (profile === null || profile === void 0 ? void 0 : profile.type) == "account" || false,
    needPasscode: profile => !!values(profile, "passcode"),
    icon: profile => values(profile, "icon"),
    rating: profile => values(profile, "rating"),
    age: user => calcAge(user.birthday)
  },
  FavoriteMeta: {
    __resolveType: metaResolveType
  }
}, {
  // "Mutation.logout": [isAuthenticated()],
  // "Query.userFavorites": [isAuthenticated()],
  "Query.favoritesByContentType": [isAuthenticated()],
  "Query.viewingHistories": [isAuthenticated()],
  "Mutation.selectProfile": [isAuthenticated()],
  "Mutation.addFavorite": [isAuthenticated()],
  "Mutation.deleteFavorite": [isAuthenticated()],
  "Mutation.updatePassword": [isAuthenticated(), ownerOnly()],
  "Mutation.updateOwnerProfile": [isAuthenticated(), ownerOnly()],
  "Mutation.addProfile": [isAuthenticated(), ownerOnly()],
  "Mutation.updateProfile": [isAuthenticated(), ownerOnly()],
  "Mutation.userDelete": [isAuthenticated(), ownerOnly()],
  "Mutation.advertisingPreferences": [isAuthenticated(), ownerOnly()]
});